/**
 * Parses geographic coordinates from common user-facing formats.
 * Decimal pairs follow the usual GIS convention: first value = latitude, second = longitude
 * (e.g. 41.40338, 2.17403 — Barcelona area).
 */

export type ParsedCoords = { lat: number; lng: number }

export const COORDINATE_INPUT_HINT =
  'DD : lat, lng · DMS : 41°24′12.2″N 2°10′26.5″E · DMM : 41 24.2028, 2 10.4418'

function parseDecimalPair(latRaw: number, lngRaw: number): ParsedCoords | null {
  let lat: number
  let lng: number
  if (Math.abs(latRaw) > 90) {
    lng = latRaw
    lat = lngRaw
  } else if (Math.abs(lngRaw) > 90) {
    lat = latRaw
    lng = lngRaw
  } else {
    lat = latRaw
    lng = lngRaw
  }
  if (Math.abs(lat) > 90 || Math.abs(lng) > 180) return null
  return { lat, lng }
}

/** Decimal degrees: comma, semicolon, or whitespace between two numbers. */
function parseDecimalDegrees(raw: string): ParsedCoords | null {
  const s = raw.trim()
  const reComma =
    /^(-?\d+(?:\.\d+)?)\s*[,;]\s*(-?\d+(?:\.\d+)?)$/
  const reSpace = /^(-?\d+(?:\.\d+)?)\s{1,}(-?\d+(?:\.\d+)?)$/
  const m = reComma.exec(s) ?? reSpace.exec(s)
  if (!m) return null
  const a = parseFloat(m[1])
  const b = parseFloat(m[2])
  if (!Number.isFinite(a) || !Number.isFinite(b)) return null
  return parseDecimalPair(a, b)
}

function dmmSignedPair(degStr: string, minStr: string): number {
  const deg = parseInt(degStr, 10)
  const min = parseFloat(minStr)
  if (!Number.isFinite(min)) return NaN
  const sign = deg < 0 ? -1 : 1
  return sign * (Math.abs(deg) + min / 60)
}

/** Degrees and decimal minutes: `41 24.2028, 2 10.4418` */
function parseDmm(raw: string): ParsedCoords | null {
  const m =
    /^(-?\d{1,3})\s+([\d.]+)\s*[,;]\s*(-?\d{1,3})\s+([\d.]+)$/.exec(raw.trim())
  if (!m) return null
  const lat = dmmSignedPair(m[1], m[2])
  const lng = dmmSignedPair(m[3], m[4])
  if (!Number.isFinite(lat) || !Number.isFinite(lng)) return null
  return parseDecimalPair(lat, lng)
}

function dmsToDecimal(deg: string, min: string, sec: string, hemi: string): number {
  const d = parseInt(deg, 10)
  const mn = parseInt(min, 10)
  const sc = parseFloat(sec)
  if (!Number.isFinite(sc)) return NaN
  let v = Math.abs(d) + mn / 60 + sc / 3600
  const h = hemi.toUpperCase()
  if (h === 'S' || h === 'W') v = -v
  return v
}

/** Degrees, minutes, seconds with hemisphere, e.g. 41°24′12.2″N 2°10′26.5″E */
function parseDms(raw: string): ParsedCoords | null {
  const normalized = raw.replace(/\u2032|'/g, "'").replace(/\u2033|"/g, '"')
  const part =
    /(\d{1,3})°\s*(\d{1,2})'\s*([\d.]+)"?\s*([NSEW])/gi
  const matches = [...normalized.matchAll(part)]
  if (matches.length !== 2) return null

  let lat: number | undefined
  let lng: number | undefined
  for (const m of matches) {
    const hem = m[4]
    const dec = dmsToDecimal(m[1], m[2], m[3], hem)
    if (!Number.isFinite(dec)) return null
    const hu = hem.toUpperCase()
    if (hu === 'N' || hu === 'S') lat = dec
    else lng = dec
  }
  if (lat === undefined || lng === undefined) return null
  return parseDecimalPair(lat, lng)
}

/** Returns WGS84 lat/lng when the string fully matches a supported coordinate form. */
export function parseCoordinates(raw: string): ParsedCoords | null {
  const s = raw.trim()
  if (!s) return null

  if (/°/.test(s) || /\d'\s*[\d.]/.test(s) || /[NSEW]/i.test(s)) {
    const dms = parseDms(s)
    if (dms) return dms
  }

  const dmm = parseDmm(s)
  if (dmm) return dmm

  return parseDecimalDegrees(s)
}
